import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, ArrowLeft, Star, Zap, DollarSign, TrendingUp, FlaskConical, Repeat, Building2, Handshake, Rocket, Users, Check, Brain, Play, Eye, X } from 'lucide-react';
import { aiEvaluatorsAPI, programsAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { PaywallModal } from '../../components/PaywallModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const ICON_MAP = {
  'dollar-sign': DollarSign,
  'trending-up': TrendingUp,
  'flask-conical': FlaskConical,
  'repeat': Repeat,
  'building-2': Building2,
  'handshake': Handshake,
  'rocket': Rocket,
  'users': Users,
};

export default function AIEvaluators() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();

  const [profiles, setProfiles] = useState([]);
  const [program, setProgram] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [results, setResults] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [profileModal, setProfileModal] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userId = user?.id || user?._id;
        const [profilesData, programData, planData] = await Promise.all([
          aiEvaluatorsAPI.getProfiles(),
          programsAPI.getById(programId),
          userId ? programsAPI.getPlanInfo(userId).catch(() => null) : null
        ]);
        setProfiles(profilesData);
        setProgram(programData);
        if (planData) setPlanInfo(planData);
      } catch (e) {
        console.error('Error loading AI profiles:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [programId, user]);

  const isFree = planInfo?.is_free;
  const maxAIFree = 1;

  const toggleProfile = (slug) => {
    if (selected.includes(slug)) {
      setSelected(prev => prev.filter(s => s !== slug));
    } else {
      if (isFree && selected.length >= maxAIFree) {
        setPaywallOpen(true);
        return;
      }
      setSelected(prev => [...prev, slug]);
    }
  };

  const handleEvaluate = async () => {
    if (selected.length === 0) {
      toast({ title: language === 'es' ? 'Selecciona al menos un evaluador' : 'Select at least one evaluator', variant: 'destructive' });
      return;
    }
    try {
      setEvaluating(true);
      const userId = user?.id || user?._id;
      const res = await aiEvaluatorsAPI.evaluate(programId, selected, userId);
      setResults(res);
      toast({
        title: language === 'es' ? 'Evaluacion completada' : 'Evaluation completed',
        description: language === 'es'
          ? `${res.evaluated} evaluaciones realizadas, ${res.errors} errores`
          : `${res.evaluated} evaluations completed, ${res.errors} errors`
      });
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail.startsWith('PLAN_LIMIT')) {
        setPaywallOpen(true);
      } else {
        toast({ title: 'Error', description: detail || e.message, variant: 'destructive' });
      }
    } finally {
      setEvaluating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eef0f4] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const t = (es, en) => language === 'es' ? es : en;

  return (
    <div className="min-h-screen bg-[#eef0f4]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4 text-gray-500 hover:text-gray-700" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t('Volver', 'Back')}
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <Brain className="w-7 h-7 text-purple-600" />
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="page-title">
                  {t('Evaluadores IA', 'AI Evaluators')}
                </h1>
              </div>
              <p className="text-gray-500 mt-1 ml-10">
                {program?.name} — {t(
                  'Selecciona los evaluadores y luego inicia la evaluacion',
                  'Select evaluators and then start the evaluation'
                )}
              </p>
              {isFree && (
                <div className="ml-10 mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="free-plan-ai-limit-badge">
                  <Zap className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-800 font-medium">
                    {t(
                      `Plan gratuito: puedes usar ${maxAIFree} evaluador IA.`,
                      `Free plan: you can use ${maxAIFree} AI evaluator.`
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Results banner */}
      {results && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-3" data-testid="results-banner">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <Check className="w-5 h-5 text-emerald-600" />
            <p className="text-sm text-emerald-800 font-medium">
              {t(
                `Evaluacion completada: ${results.evaluated} evaluaciones exitosas, ${results.errors} errores.`,
                `Evaluation complete: ${results.evaluated} successful, ${results.errors} errors.`
              )}
            </p>
            <Button variant="outline" size="sm" className="ml-auto border-emerald-300 text-emerald-700"
              onClick={() => navigate(`/organization/challenge/${programId}`)}>
              {t('Ver resultados', 'View results')}
            </Button>
          </div>
        </div>
      )}

      {/* Profile Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {profiles.map((profile, idx) => {
            const isSelected = selected.includes(profile.slug);
            const titleText = profile.title?.[language] || profile.title?.es || '';
            const badgeTexts = (profile.badges || []).map(b => b.text?.[language] || b.text?.es || '');
            const expertiseList = (profile.expertise_bullets?.[language] || profile.expertise_bullets?.es || []);

            return (
              <Card
                key={profile.slug}
                className={`relative overflow-hidden transition-all duration-300 cursor-pointer rounded-2xl border-2 bg-white ${
                  isSelected ? 'border-blue-400 shadow-lg ring-2 ring-blue-200' : 'border-gray-100 hover:border-gray-200 hover:shadow-md'
                }`}
                onClick={() => toggleProfile(profile.slug)}
                data-testid={`ai-profile-card-${profile.slug}`}
              >
                <CardContent className="p-5">
                  {/* Top: Avatar + Name + Toggle */}
                  <div className="flex items-start gap-3 mb-3">
                    <img
                      src={profile.avatar}
                      alt={profile.name}
                      className="w-16 h-16 rounded-xl object-cover border-2 border-gray-100 shadow-sm flex-shrink-0"
                      data-testid={`avatar-${profile.slug}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-base font-bold text-gray-900 truncate" data-testid={`name-${profile.slug}`}>
                          {profile.name}
                        </h3>
                        {/* Toggle switch */}
                        <div
                          className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors flex-shrink-0 ${
                            isSelected ? 'bg-blue-500' : 'bg-gray-300'
                          }`}
                          data-testid={`toggle-${profile.slug}`}
                        >
                          <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${isSelected ? 'translate-x-5' : 'translate-x-0'}`} />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 font-medium">{titleText}</p>
                      {/* Stars */}
                      <div className="flex items-center gap-0.5 mt-1" data-testid={`rating-${profile.slug}`}>
                        {[1, 2, 3, 4, 5].map(i => (
                          <Star
                            key={i}
                            className={`w-3.5 h-3.5 ${i <= Math.floor(profile.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {badgeTexts.map((text, bi) => (
                      <span
                        key={bi}
                        className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700"
                      >
                        {text}
                      </span>
                    ))}
                  </div>

                  {/* Expertise bullets */}
                  {expertiseList.length > 0 && (
                    <ul className="space-y-1 mb-3">
                      {expertiseList.map((item, ei) => (
                        <li key={ei} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2 mt-1">
                    <button
                      className="flex-1 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        setProfileModal(profile);
                      }}
                      data-testid={`view-profile-${profile.slug}`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      {t('Ver perfil', 'View profile')}
                    </button>
                    <button
                      className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                        isSelected
                          ? 'bg-blue-500 text-white border border-blue-500'
                          : 'text-blue-600 border border-gray-200 hover:bg-blue-50'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleProfile(profile.slug);
                      }}
                      data-testid={`select-btn-${profile.slug}`}
                    >
                      {isSelected ? t('Seleccionado', 'Selected') : t('Seleccionar', 'Select')}
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Action bar - shows when evaluators are selected */}
        {selected.length > 0 && !results && (
          <div className="mt-8 bg-white rounded-xl border-2 border-purple-200 p-6 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm" data-testid="action-bar">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Brain className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  {selected.length} {t('evaluador(es) seleccionado(s)', 'evaluator(s) selected')}
                </p>
                <p className="text-sm text-gray-500">
                  {t('Se evaluaran todas las postulaciones del desafio', 'All challenge applications will be evaluated')}
                </p>
              </div>
            </div>
            <Button
              onClick={handleEvaluate}
              disabled={evaluating}
              className="text-white font-semibold px-8 py-3 text-base"
              style={{ backgroundColor: '#6949b6' }}
              data-testid="evaluate-btn"
            >
              {evaluating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('Evaluando...', 'Evaluating...')}</>
              ) : (
                <><Play className="w-4 h-4 mr-2" /> {t('Iniciar evaluacion', 'Start evaluation')}</>
              )}
            </Button>
          </div>
        )}

        {/* Scoring criteria info */}
        {program?.scoringCriteria?.length > 0 && (
          <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6" data-testid="criteria-info">
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-4">
              {t('Criterios del desafio que seran evaluados', 'Challenge criteria to be evaluated')}
            </h3>
            <div className="flex flex-wrap gap-3">
              {program.scoringCriteria.map((c, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                  <span className="text-sm font-semibold text-gray-800">{c.name}</span>
                  <span className="text-xs text-gray-400">peso {c.weight} | max {c.maxScore}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Profile Detail Modal */}
      <Dialog open={!!profileModal} onOpenChange={() => setProfileModal(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="profile-modal">
          {profileModal && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <img
                    src={profileModal.avatar}
                    alt={profileModal.name}
                    className="w-16 h-16 rounded-xl object-cover border-2 border-gray-100 shadow-sm"
                  />
                  <div>
                    <DialogTitle className="text-xl">{profileModal.name}</DialogTitle>
                    <p className="text-sm text-gray-500 font-medium">
                      {profileModal.title?.[language] || profileModal.title?.es || ''}
                    </p>
                    <div className="flex items-center gap-0.5 mt-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Star
                          key={i}
                          className={`w-3.5 h-3.5 ${i <= Math.floor(profileModal.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </DialogHeader>
              <div className="mt-4">
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {(profileModal.badges || []).map((b, i) => (
                    <span key={i} className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">
                      {b.text?.[language] || b.text?.es || ''}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {profileModal.bio?.[language] || profileModal.bio?.es || t('Sin descripcion disponible.', 'No description available.')}
                </p>
              </div>
              <div className="mt-6 flex justify-end">
                <Button
                  className="text-white font-medium px-6"
                  style={{ backgroundColor: '#6949b6' }}
                  onClick={() => {
                    if (!selected.includes(profileModal.slug)) toggleProfile(profileModal.slug);
                    setProfileModal(null);
                  }}
                  data-testid="modal-select-btn"
                >
                  {selected.includes(profileModal.slug)
                    ? t('Ya seleccionado', 'Already selected')
                    : t('Seleccionar evaluador', 'Select evaluator')}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} trigger="ai_evaluators" />
    </div>
  );
}
